// Apollo does not return correct prototype to use it directly
import {
    FolderGridItemObject,
    FolderWorkplaceObject,
    PersonGridItemObject,
    RelatedFolderObject,
    ServiceGridItemObject,
} from '@it2go/types';
import { CommonServiceSettingsType } from '@libs/types/src/services/types/common-service-settings.type';
import { FolderPersonObject } from '@libs/types/src/services/object-type/folder/folder-person.object';
import { formatPerson } from '@libs/shared/helpers/person.utils';

/**
 * Popisek složky (drobečková navigace, historie návštěv) - jména všech primárních (isMain) osob,
 * vlastník složky první. Bez osob ve složce (starší data, vypnuté více osob) zůstává jen vlastník.
 */
export function folderPersonsLabel(
    ownerPerson?: PersonGridItemObject | null,
    folderPersons?: FolderPersonObject[] | null,
): string {
    const names = folderMainPersonStays(ownerPerson, folderPersons).map((it) => formatPerson(it.person));

    return names.length ? names.join(', ') : formatPerson(ownerPerson);
}

/** Primární (isMain) osoby složky, vlastník první. */
export function folderMainPersonStays(
    ownerPerson?: PersonGridItemObject | null,
    folderPersons?: FolderPersonObject[] | null,
): FolderPersonObject[] {
    return ownerFirst(
        ownerPerson,
        (folderPersons || []).filter((it) => it.isMain && !!it.person),
    );
}

/** Osoby, které jsou v daný okamžik na službě - primární i ne, vlastník první. */
export function folderActivePersonStays(
    ownerPerson?: PersonGridItemObject | null,
    folderPersons?: FolderPersonObject[] | null,
    at: Date = new Date(),
): FolderPersonObject[] {
    return ownerFirst(
        ownerPerson,
        (folderPersons || [])
            .filter((it) => !!it.person && !it.isDeceased && isFolderPersonActive(it, at)),
    );
}

function ownerFirst(
    ownerPerson: PersonGridItemObject | null | undefined,
    stays: FolderPersonObject[],
): FolderPersonObject[] {
    return [
        ...stays.filter((it) => it.personId === ownerPerson?.id),
        ...stays.filter((it) => it.personId !== ownerPerson?.id),
    ];
}

/**
 * Osoba je na službě, jen když její pobyt už začal a ještě neskončil - budoucí ani ukončený se
 * nepočítá. Být ve složce je něco jiného: ukončení pobytu v historii osobu ze složky neodebere
 * (to umí jen akce "Odebrat ze složky"), takže osoba ve složce klidně na službě být nemusí.
 */
export function isFolderPersonActive(folderPerson?: FolderPersonObject | null, at: Date = new Date()): boolean {
    if (!folderPerson?.dateFrom) return false;

    return new Date(folderPerson.dateFrom) <= at
        && (!folderPerson.dateTo || new Date(folderPerson.dateTo) >= at);
}

export function currentFolderWorkplace(self?: FolderGridItemObject | null): FolderWorkplaceObject | null {
    return activeFolderWorkplace(self?.workplaces);
}

export function activeFolderWorkplace(workplaces?: FolderWorkplaceObject[] | null): FolderWorkplaceObject | null {
    const now = new Date();
    return workplaces?.find(
        (it) => (!it.dateFrom || new Date(it.dateFrom) < now) && (!it.dateTo || new Date(it.dateTo) > now),
    ) || null;
}

export function getSharingFolderIds(
    currentServiceId: number | undefined,
    services: ServiceGridItemObject[],
    folders: RelatedFolderObject[] | undefined,
    key: string,
): number[] | undefined {
    if (!currentServiceId) return [];

    const serviceIds = services
        .filter((service) => {
            if (service.id === currentServiceId) return false;
            const serviceSettings = service.serviceSettings?.find((it) => it.key === key);
            const serviceSettingsData = JSON.parse(serviceSettings?.data || '{}') as CommonServiceSettingsType;
            return serviceSettingsData.shareWithOtherServices?.includes(currentServiceId) ?? false;
        })
        .map((service) => service.id);

    if (serviceIds?.length && folders?.length) {
        return folders?.filter(f => serviceIds.includes(f.serviceId))?.map(f => f.id) || [];
    } else {
        return []
    }
}
